import React, { Component } from 'react';
import PropTypes from 'prop-types';
import memoize from 'memoize-one';
import cn from 'classnames';
import d3 from '../../utils/d3-import';
import styles from './BarChart.css';
import { isMobileWidth } from '../../theme/layoutUtilities';
import { comparisonColors } from '../../utils/colors';
import { responsiveAxes } from '../../utils/chart-util';

class BarChart extends Component {
    static propTypes = {
        /**
         * Data to pass to the chart
         */
        data: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                headers: PropTypes.arrayOf(PropTypes.string),
                data: PropTypes.instanceOf(Array)
            })
        ),
        /**
         * Default height of the chart
         */
        height: PropTypes.number,
        /**
         * Width of the chart
         */
        width: PropTypes.number,
        /**
         * Bottom margin for chart
         */
        marginBottom: PropTypes.number,
        /**
         * Left margin for chart
         */
        marginLeft: PropTypes.number,
        /**
         * Function to round the y axis values
         */
        roundYAxis: PropTypes.func,
        /**
         * Shows/hides the tooltip
         */
        toggleTooltip: PropTypes.func,
        /**
         * Updates the data associated with the tool tip
         */
        updateTooltip: PropTypes.func,
        /**
         * Format for Y-axis labels. D3 format
         */
        formatYAxis: PropTypes.string,
        /**
         * Format for Y-Axis labels when axis range is < a certain value
         */
        formatYAxisDec: PropTypes.string,
        /**
         * Function to format X-Axis lables with regex
         * [[textToReplace, replaceWith]...]
         */
        formatXAxis: PropTypes.arrayOf(
            PropTypes.arrayOf(
                PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.instanceOf(RegExp)
                ])
            )
        )
    };

    static defaultProps = {
        data: [
            {
                label: '',
                headers: [],
                data: []
            }
        ],
        height: 400,
        width: 800,
        marginLeft: 65,
        marginBottom: 25,
        roundYAxis: (num) => num * 1.25,
        toggleTooltip: () => {},
        updateTooltip: () => {},
        formatYAxis: '$,.0f',
        formatYAxisDec: '$,.2f',
        formatXAxis: [[]]
    };

    constructor(props) {
        super(props);
        this.tooltip = React.createRef();
        this.xAxisRef = React.createRef();
        this.yAxisRef = React.createRef();
        this.gridlines = React.createRef();
        this.state = {
            bars: [],
            hoverEle: '',
            xScaleGroup: {},
            yScale: {}
        };
    }

    /**
     * Contains the axis logic that is handled by D3
     */
    componentDidUpdate() {
        const { width, formatXAxis, formatYAxis, formatYAxisDec } = this.props;
        const { yScale } = this.state;
        const yAxis = d3.select(this.yAxisRef.current);
        const xAxis = d3.select(this.xAxisRef.current);
        this.xAxis = d3.axisBottom().scale(this.state.xScaleGroup);
        const formatter =
            yScale.domain()[1] - yScale.domain()[0] < 0.06
                ? formatYAxisDec
                : formatYAxis;
        this.yAxis = d3
            .axisLeft()
            .scale(this.state.yScale)
            .tickFormat(d3.format(formatter));
        xAxis.call(this.xAxis);
        yAxis.call(this.yAxis);
        d3.select(this.gridlines.current).call(
            d3
                .axisLeft(this.state.yScale)
                .ticks(8)
                .tickSize(-width)
                .tickFormat('')
        );

        responsiveAxes(width, yAxis, xAxis, formatXAxis);
    }

    onMouseMove = (e, d) => {
        const { target } = e;
        const dim = target.parentNode.parentNode.getBoundingClientRect();
        const x = e.clientX - dim.left;
        const y = e.clientY - dim.top;
        this.onMove(x, y, d);
    };

    onMouseEnter = (e, d) => {
        const { toggleTooltip } = this.props;
        this.setState({
            hoverEle: d.data.label
        });
        toggleTooltip(true);
    };

    onTouchStart = (e, d) => {
        const { target } = e;
        const { touches } = e;

        if (!touches.length) return;

        const lastTouch = touches[touches.length - 1];
        const dim = target.parentNode.parentNode.getBoundingClientRect();
        const x = lastTouch.clientX - dim.left;
        const y = lastTouch.clientY - dim.top;

        const { updateTooltip } = this.props;

        this.setState({
            hoverEle: d.data.label
        });
        updateTooltip(x, y, d, true);
    };

    onLeave = () => {
        const { toggleTooltip } = this.props;
        this.setState({
            hoverEle: ''
        });
        toggleTooltip(false);
    };

    onMove = (x, y, d) => {
        const { updateTooltip } = this.props;
        updateTooltip(x, y, d);
    };

    /**
     * Recalculates chart when dimensions change
     */
    static getDerivedStateFromProps(nextProps) {
        const {
            data,
            width,
            height,
            marginBottom,
            marginLeft,
            roundYAxis
        } = nextProps;
        const xDomain = BarChart.calcXDomain(data);
        const yDomain = BarChart.calcYDomain(data);
        const xScaleGroup = BarChart.calcXScaleGroup(
            marginLeft,
            width,
            xDomain
        );
        const xScale = BarChart.calcXScale(marginLeft, width, xDomain);
        const yScale = BarChart.calcYScale(
            height,
            marginBottom,
            yDomain,
            roundYAxis
        );
        const bars = BarChart.calcBars(data, xScaleGroup, xScale, yScale);
        return {
            bars,
            xScaleGroup,
            yScale
        };
    }

    static calcXScaleGroup = memoize((marginLeft, width, xDomain) =>
        d3
            .scaleBand()
            .rangeRound([marginLeft, width])
            .paddingInner(width < 400 ? 0.15 : 0.3)
            .paddingOuter(0.4)
            .domain(xDomain)
    );

    static calcYScale = memoize((height, marginBottom, yDomain, roundYAxis) => {
        const [yMin, yMax] = yDomain;
        return d3
            .scaleLinear()
            .range([height - marginBottom, marginBottom])
            .domain([roundYAxis(yMin), roundYAxis(yMax)]);
    });

    static calcXScale = memoize((marginLeft, width, xDomain) =>
        d3
            .scaleBand()
            .range([marginLeft, width - marginLeft])
            .paddingInner(0.15)
            .paddingOuter(0.4)
            .domain(xDomain)
    );

    static calcXDomain = memoize((data) => data.map((d) => d.label));

    static calcYDomain = memoize((data) => {
        const [yMin, yMax] = d3.extent(
            data.reduce((a, v) => a.concat(v.data), [])
        );
        return [Math.min(yMin, 0), yMax];
    });

    static calcBars = memoize((data, xScaleGroup, xScale, yScale) =>
        data.map((d) => {
            const xSubDomain = d3.range(0, d.data.length);
            xScale.domain(xSubDomain).rangeRound([0, xScaleGroup.bandwidth()]);
            const yZero = yScale(0);
            const barXArr = [];
            const barYArr = [];
            const barHeightArr = [];
            d.data.forEach((ele, i) => {
                barYArr.push(yScale(Math.max(ele, 0)));
                barXArr.push(xScale(i));
                barHeightArr.push(Math.abs(yScale(ele) - yZero));
            });

            const barWidth = xScale.bandwidth();
            return {
                key: d.label,
                data: d,
                width: xScale.bandwidth(),
                groupWidth: xScaleGroup.bandwidth(),
                group: xScaleGroup(d.label),
                bars: d.data.map((ele, i) => ({
                    label: d.headers[i],
                    height: barHeightArr[i],
                    width: barWidth,
                    x: barXArr[i],
                    y: barYArr[i],
                    fill: comparisonColors[i]
                }))
            };
        })
    );

    render() {
        const { height, width, marginBottom, marginLeft } = this.props;
        const { bars, hoverEle } = this.state;
        const isMobile = isMobileWidth();

        return (
            <svg width={width} height={height}>
                <g
                    className="bar-gridlines"
                    ref={this.gridlines}
                    transform={`translate(${marginLeft}, 0)`}
                />
                {bars.map((d) => {
                    const ix = {};

                    if (isMobile) {
                        ix.onTouchStart = (e) => this.onTouchStart(e, d);
                    } else {
                        ix.onMouseEnter = (e) => this.onMouseEnter(e, d);
                        ix.onMouseMove = (e) => this.onMouseMove(e, d);
                        ix.onMouseLeave = this.onLeave;
                    }

                    return (
                        <g
                            key={`${d.key}`}
                            className={cn('bar-group', {
                                focus: hoverEle === d.data.label,
                                blur:
                                    hoverEle !== '' && hoverEle !== d.data.label
                            })}
                            transform={`translate(${d.group}, 0)`}
                        >
                            {d.bars &&
                                d.bars.map((bar) => (
                                    <rect
                                        key={`${d.key}-${bar.label}`}
                                        x={bar.x}
                                        y={bar.y}
                                        width={bar.width}
                                        height={bar.height}
                                        fill={bar.fill}
                                    />
                                ))}
                            <rect
                                width={d.groupWidth}
                                className="bar-hover-area"
                                height={height - 2 * marginBottom}
                                fillOpacity="0"
                                transform={`translate(${0}, ${marginBottom})`}
                                {...ix}
                            />
                        </g>
                    );
                })}
                <g>
                    <g
                        ref={this.xAxisRef}
                        transform={`translate(0, ${height - marginBottom})`}
                        className="x-axis"
                    />
                    <g
                        ref={this.yAxisRef}
                        transform={`translate(${marginLeft}, 0)`}
                        className="y-axis"
                    />
                </g>
                <style jsx>{styles}</style>
            </svg>
        );
    }
}

export default BarChart;
